import { useRef, useState } from 'react'

import { api } from '@/lib/api'
import type { PlanRequest } from '@/lib/types'

/**
 * Identifiant d'ordre, avec repli.
 *
 * `crypto.randomUUID` est réservé aux contextes sécurisés et absent des
 * navigateurs un peu anciens. Le repli tire ses octets de `getRandomValues`
 * quand il est là, et retombe sinon sur l'horloge et `Math.random` : cet
 * identifiant ne protège aucun secret, il sert seulement à ce que le serveur
 * reconnaisse un ordre déjà passé si la réponse se perd. Unique suffit.
 */
function newOrderId(): string {
  const random = globalThis.crypto
  if (typeof random?.randomUUID === 'function') return random.randomUUID()
  if (typeof random?.getRandomValues === 'function') {
    const bytes = random.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/**
 * Envoi d'un ordre, avec idempotence.
 *
 * Un seul endroit dans toute l'application sait envoyer un ordre. C'est
 * délibéré : chaque chemin d'envoi supplémentaire serait un endroit de plus où
 * oublier `confirmed: true` ou l'identifiant d'ordre. Le ticket et le bouton
 * posé sur le graphique passent tous les deux par ici.
 *
 * L'identifiant est engendré **avant** l'envoi et conservé tant que l'ordre
 * n'est pas passé : si la réponse se perd en route, réessayer présente le même
 * identifiant et le serveur reconnaît l'ordre déjà exécuté au lieu d'en ouvrir
 * un second.
 */
export function useSendOrder() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const orderId = useRef<string | null>(null)

  async function send(request: PlanRequest) {
    if (busy) return false
    setBusy(true)
    setError(null)

    try {
      // DANS le try, et non avant.
      //
      // `crypto.randomUUID` n'existe pas partout : ni hors contexte sécurisé,
      // ni sur les Safari antérieurs à 15.4. L'appel était placé avant le
      // `try` : une exception s'échappait de `send`, `busy` restait vrai pour
      // toujours, et le bouton d'envoi se figeait sur « Envoi… », grisé, sans
      // le moindre message. On voyait la feuille de confirmation et on ne
      // pouvait plus rien valider — le pire échec possible ici, puisqu'il
      // ressemble à une application cassée plutôt qu'à un refus.
      orderId.current ??= newOrderId()

      const response = await api.sendOrder({
        ...request,
        confirmed: true,
        client_order_id: orderId.current,
      })
      setResult(response.message)
      orderId.current = null
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Envoi impossible')
      return false
    } finally {
      setBusy(false)
    }
  }

  function reset() {
    setResult(null)
    setError(null)
  }

  return { busy, result, error, send, reset }
}
